import is from '@sindresorhus/is';

import type { TileRegistration } from './types';

const UNKEYED_WEIGHT = Number.POSITIVE_INFINITY;

// Pinned-right tiles keep their pin regardless of a key: the pin is a stronger placement
// statement than the caller's ordering.
const orderWeight = (tile: TileRegistration): number =>
    tile.pinRight === true || is.undefined(tile.orderKey) ? UNKEYED_WEIGHT : tile.orderKey;

/**
 * Places keyed registrations at their `orderKey` position instead of at the end of the
 * registration list, so a tile whose portal mounts later than its neighbours (a runtime
 * toggle) still renders where its owner asked for it.
 *
 * Keyed tiles lead in ascending `orderKey`; unkeyed and pinned-right tiles follow in
 * registration order. A registry with no keys is returned untouched, so callers that never
 * pass an `orderKey` keep the plain append semantics.
 */
export const orderRegisteredTiles = (tiles: readonly TileRegistration[]): readonly TileRegistration[] => {
    if (tiles.every((tile) => is.undefined(tile.orderKey))) {
        return tiles;
    }

    return [...tiles].sort((first, second) => {
        const firstWeight = orderWeight(first);
        const secondWeight = orderWeight(second);

        if (firstWeight === secondWeight) {
            return 0;
        }

        return firstWeight < secondWeight ? -1 : 1;
    });
};
